import { Outlet, useNavigate } from 'react-router-dom';
import { LogOut, Menu } from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { Sidebar } from '@/components/layout/Sidebar';
import { MobileNav } from '@/components/layout/MobileNav';
import { ProcessingOverlay } from '@/components/dashboard/ProcessingOverlay';
import { DownloadReadyModal } from '@/components/dashboard/DownloadReadyModal';
import { AdGateModal } from '@/components/dashboard/AdGateModal';
import { ShortcutsHelp } from '@/components/dashboard/ShortcutsHelp';
import { FOCUS_URL_EVENT, PASTE_URL_EVENT } from '@/components/dashboard/UrlInput';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { useQueueSync } from '@/hooks/useDownloads';
import { useI18n } from '@/hooks/useI18n';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { logout } from '@/services/authService';
import { useUiStore } from '@/store/uiStore';

export function DashboardLayout() {
  const { status, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  const setShortcutsOpen = useUiStore((s) => s.setShortcutsOpen);

  useQueueSync();

  useKeyboardShortcuts({
    '/': () => window.dispatchEvent(new Event(FOCUS_URL_EVENT)),
    'mod+k': () => window.dispatchEvent(new Event(FOCUS_URL_EVENT)),
    v: () => window.dispatchEvent(new Event(PASTE_URL_EVENT)),
    '?': () => setShortcutsOpen(true),
    escape: () => setShortcutsOpen(false),
  });

  const handleSignOut = async () => {
    await logout();
    navigate(ROUTES.home);
  };

  return (
    <div className="min-h-screen">
      <Sidebar />

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-white/5 bg-surface/80 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="rounded-xl p-2 text-zinc-300 hover:bg-white/5 focus-ring lg:hidden"
              aria-label="Open sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="lg:hidden">
              <Logo withText={false} />
            </span>
          </div>

          <div className="flex items-center gap-3">
            {status === 'signedIn' ? (
              <>
                <span className="hidden text-sm text-zinc-400 sm:block">
                  {user?.displayName ?? user?.email}
                </span>
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="h-9 w-9 rounded-full border border-white/10"
                  />
                ) : (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500/20 text-sm font-bold text-primary-300">
                    {(user?.displayName ?? user?.email ?? 'U').charAt(0).toUpperCase()}
                  </span>
                )}
                <Button variant="ghost" size="sm" onClick={handleSignOut} aria-label={t('nav.signOut')}>
                  <LogOut className="h-4 w-4" aria-hidden />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.login)}>
                  {t('nav.signIn')}
                </Button>
                <Button size="sm" onClick={() => navigate(ROUTES.register)}>
                  {t('nav.getStarted')}
                </Button>
              </>
            )}
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-4 py-6 pb-mobile-nav sm:px-6 sm:py-8 lg:pb-8">
          <Outlet />
        </main>
      </div>

      <MobileNav />
      <ProcessingOverlay />
      <DownloadReadyModal />
      <AdGateModal />
      <ShortcutsHelp />
    </div>
  );
}
